import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Paperclip, FileText, Trash2, Upload, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuthContext } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { formatDate } from '@/lib/utils';
import { apiFetch } from '@/lib/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Attachment {
  id: number;
  module: string;
  record_id: number;
  file_path: string | null;
  original_name: string | null;
  note: string | null;
  uploaded_by: number;
  uploaded_by_name: string | null;
  created_at: string;
}

interface AttachmentsResponse {
  data: { attachments: Attachment[] };
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface AttachmentsPanelProps {
  module: string;
  recordId: number;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function AttachmentsPanel({ module, recordId }: AttachmentsPanelProps) {
  const { user } = useAuthContext();
  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // UI state
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [collapsed, setCollapsed] = useState(false);

  // Query
  const queryKey = ['attachments', module, recordId] as const;
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () =>
      apiFetch<AttachmentsResponse>(
        `/api/attachments?module=${encodeURIComponent(module)}&record_id=${recordId}`,
      ),
  });

  const attachments = data?.data.attachments ?? []; // server returns newest first (created_at DESC)

  function invalidate() {
    queryClient.invalidateQueries({ queryKey });
  }

  // ── Add note ──
  async function handleAddNote() {
    const text = noteText.trim();
    if (!text) return;
    setSavingNote(true);
    try {
      const fd = new FormData();
      fd.append('module', module);
      fd.append('record_id', String(recordId));
      fd.append('note', text);
      const res = await fetch('/api/attachments', {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
      }
      toast.success('Note added');
      setNoteText('');
      setAddingNote(false);
      invalidate();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add note');
    } finally {
      setSavingNote(false);
    }
  }

  // ── Upload file ──
  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append('module', module);
      fd.append('record_id', String(recordId));
      fd.append('file', file);
      const res = await fetch('/api/attachments', {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
      }
      toast.success('File uploaded');
      invalidate();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  }

  // ── Delete ──
  async function handleDelete(attachmentId: number) {
    setDeletingId(attachmentId);
    try {
      await apiFetch(`/api/attachments/${attachmentId}`, { method: 'DELETE' });
      toast.success('Deleted');
      setConfirmDeleteId(null);
      invalidate();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  }

  // ── Render ──
  return (
    <div className="rounded-lg border bg-card">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="flex items-center gap-2 text-sm font-semibold text-muted-foreground uppercase tracking-wide hover:text-foreground transition-colors"
        >
          <Paperclip className="h-3.5 w-3.5" />
          Attachments &amp; Notes
          {!isLoading && attachments.length > 0 && (
            <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
              {attachments.length}
            </span>
          )}
          {collapsed ? (
            <ChevronDown className="h-3.5 w-3.5 ml-1" />
          ) : (
            <ChevronUp className="h-3.5 w-3.5 ml-1" />
          )}
        </button>

        {/* Action buttons */}
        {!collapsed && (
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileChange}
            />
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingFile}
            >
              <Upload className="h-3 w-3 mr-1" />
              {uploadingFile ? 'Uploading…' : 'Upload File'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => setAddingNote((v) => !v)}
            >
              <MessageSquare className="h-3 w-3 mr-1" />
              Add Note
            </Button>
          </div>
        )}
      </div>

      {!collapsed && (
        <>
          {/* Add note inline form */}
          {addingNote && (
            <div className="px-4 py-3 border-b bg-muted/20">
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Type a note…"
                rows={3}
                disabled={savingNote}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none mb-2"
              />
              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setAddingNote(false);
                    setNoteText('');
                  }}
                  disabled={savingNote}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleAddNote}
                  disabled={savingNote || !noteText.trim()}
                >
                  {savingNote ? 'Saving…' : 'Save Note'}
                </Button>
              </div>
            </div>
          )}

          {/* Attachment list */}
          {isLoading ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">Loading…</div>
          ) : attachments.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              No attachments or notes yet.
            </div>
          ) : (
            <ul className="divide-y">
              {attachments.map((att) => {
                const isFile = !!att.original_name;
                const isConfirmingDelete = confirmDeleteId === att.id;
                return (
                  <li
                    key={att.id}
                    className="flex items-start gap-3 px-4 py-3 hover:bg-muted/20 transition-colors group"
                  >
                    {/* Icon */}
                    <div className="mt-0.5 shrink-0 text-muted-foreground">
                      {isFile ? (
                        <Paperclip className="h-4 w-4" />
                      ) : (
                        <FileText className="h-4 w-4" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      {isFile ? (
                        <a
                          href={`/api/attachments/${att.id}/download`}
                          className="text-sm font-medium text-foreground hover:underline truncate block"
                          download
                        >
                          {att.original_name}
                        </a>
                      ) : (
                        <p className="text-sm text-foreground whitespace-pre-wrap break-words">
                          {att.note}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {att.uploaded_by_name ?? 'Unknown'} · {formatDate(att.created_at)}
                      </p>
                    </div>

                    {/* Delete (admin only) */}
                    {isAdmin && (
                      <div className="shrink-0">
                        {isConfirmingDelete ? (
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs text-muted-foreground">Delete?</span>
                            <button
                              type="button"
                              onClick={() => handleDelete(att.id)}
                              disabled={deletingId === att.id}
                              className="text-xs font-medium text-destructive hover:underline disabled:opacity-50"
                            >
                              {deletingId === att.id ? 'Deleting…' : 'Yes'}
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmDeleteId(null)}
                              className="text-xs text-muted-foreground hover:text-foreground"
                            >
                              No
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setConfirmDeleteId(att.id)}
                            className="rounded p-1 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors opacity-0 group-hover:opacity-100"
                            title="Delete"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
